/**
 * ItemPoolCombobox — inline searchable single-select for referencing an
 * Item Pool by id. Built on SearchableSelect (small, preloaded option lists —
 * pool counts are always small) so it matches the app's existing combobox
 * chrome instead of inventing a new one.
 */

import { useMemo } from 'react';
import { SearchableSelect } from '../../../shared/SearchableSelect';
import { useItemPoolsStore } from '../../../../stores/prioritization/itemPoolsStore';
import { useTrackStore } from '../../../../stores/prioritization/trackStore';
import { useDegapProcessStore } from '../../../../stores/prioritization/degapProcessStore';
import { ITEM_POOL_STATUS_LABELS } from '../../../../types/prioritization/lifecycle';

export interface ItemPoolComboboxProps {
  value: string;
  onChange: (poolId: string) => void;
  /** Scope options to a specific cycle (optional). */
  cycleId?: string;
  /** Scope options to a specific track (optional). */
  trackId?: string;
  placeholder?: string;
  disabled?: boolean;
  className?: string;
}

export function ItemPoolCombobox({
  value,
  onChange,
  cycleId,
  trackId,
  placeholder = 'Select a pool…',
  disabled,
  className,
}: ItemPoolComboboxProps) {
  const itemPools = useItemPoolsStore((s) => s.itemPools);
  const tracks = useTrackStore((s) => s.tracks);
  const cycles = useDegapProcessStore((s) => s.cycles);

  const trackNameById = useMemo(() => Object.fromEntries(tracks.map((t) => [t.id, t.name])), [tracks]);
  const cycleNameById = useMemo(() => Object.fromEntries(cycles.map((c) => [c.id, c.name])), [cycles]);

  const options = useMemo(() => {
    return itemPools
      .filter((p) => (cycleId ? p.cycleId === cycleId : true) && (trackId ? p.trackId === trackId : true))
      .sort((a, b) => a.name.localeCompare(b.name))
      .map((p) => {
        const context = [
          p.trackId ? trackNameById[p.trackId] : null,
          p.cycleId ? cycleNameById[p.cycleId] : null,
        ]
          .filter(Boolean)
          .join(' · ');
        const statusLabel = ITEM_POOL_STATUS_LABELS[p.status] ?? p.status;
        const label = `${p.name} (${p.priorityIds.length} item${p.priorityIds.length !== 1 ? 's' : ''} · ${statusLabel}${context ? ` · ${context}` : ''})`;
        return { value: p.id, label };
      });
  }, [itemPools, cycleId, trackId, trackNameById, cycleNameById]);

  return (
    <SearchableSelect
      options={options}
      value={value}
      onChange={onChange}
      placeholder={placeholder}
      disabled={disabled}
      className={className}
    />
  );
}

export default ItemPoolCombobox;
